// Константы раздела «Электронный журнал» (журнал сроков). Справочники и права приходят с сервера (/api/v2/journal/meta)
import { BucketKey, ExportFormat, JournalFilters, OptionField, OptionPick, SortField, SortState } from './types';

export const EMPTY_PICKS: Record<OptionField, OptionPick | null> = {
  product: null,
  serial: null,
  producer: null,
  category: null,
  manager: null,
  region: null,
  priceGroup: null
};

export const EMPTY_FILTERS: JournalFilters = {
  month: '',
  pharmacies: [],
  search: '',
  bucket: '',
  expiryFrom: '',
  expiryTo: '',
  picks: EMPTY_PICKS
};

export const DEFAULT_SORT: SortState = { sortBy: 'recordDate', sortOrder: 'desc' };
export const PAGE_SIZES = [25, 50, 100, 200];
export const DEFAULT_PAGE_SIZE = 50;

/** Поля, у которых естественный порядок - по убыванию (первый клик по заголовку) */
export const DESC_FIRST: SortField[] = ['recordDate', 'updateDate', 'totalSum', 'quantity', 'quantityPcs', 'price', 'id'];

/** Фильтры с подбором значений (порядок - как в панели «Ещё фильтры») */
export const OPTION_FIELDS: Array<{ field: OptionField; label: string; hint: string }> = [
  { field: 'product', label: 'Товар', hint: 'Название, код или ШК' },
  { field: 'serial', label: 'Серия', hint: 'Номер серии' },
  { field: 'producer', label: 'Производитель', hint: 'Все производители' },
  { field: 'category', label: 'Категория', hint: 'Все категории' },
  { field: 'manager', label: 'Менеджер', hint: 'Все менеджеры' },
  { field: 'region', label: 'Регион', hint: 'Все регионы' },
  { field: 'priceGroup', label: 'Ценовая группа', hint: 'Все группы' }
];

export const OPTION_LABELS: Record<OptionField, string> = OPTION_FIELDS.reduce(
  (acc, o) => ({ ...acc, [o.field]: o.label }),
  {} as Record<OptionField, string>
);

/** Цвета корзин срока годности: от «просрочено» (красный) до «более года» (зелёный) */
export const BUCKET_COLORS: Record<BucketKey, { color: string; bg: string }> = {
  expired: { color: '#dc2626', bg: 'rgba(220,38,38,0.08)' },
  d30: { color: '#ea580c', bg: 'rgba(234,88,12,0.08)' },
  d90: { color: '#d97706', bg: 'rgba(217,119,6,0.09)' },
  d180: { color: '#0284c7', bg: 'rgba(2,132,199,0.08)' },
  d365: { color: '#2563eb', bg: 'rgba(37,99,235,0.07)' },
  more: { color: '#16a34a', bg: 'rgba(22,163,74,0.08)' },
  unknown: { color: '#64748b', bg: 'rgba(100,116,139,0.08)' }
};

export const BUCKET_LABELS: Record<BucketKey, string> = {
  expired: 'Просрочено',
  d30: 'До 30 дней',
  d90: '31–90 дней',
  d180: '91–180 дней',
  d365: '181–365 дней',
  more: 'Более года',
  unknown: 'Срок не указан'
};

/** Корзина по числу дней до истечения (границы - как на сервере) */
export function bucketOf(days: number | null): BucketKey {
  if (days === null) return 'unknown';
  if (days < 0) return 'expired';
  if (days <= 30) return 'd30';
  if (days <= 90) return 'd90';
  if (days <= 180) return 'd180';
  if (days <= 365) return 'd365';
  return 'more';
}

/** Действия в истории изменений записи (Expiry_JournalMovements.Action) */
const ACTIONS: Record<string, { label: string; color: string; bg: string }> = {
  CREATE: { label: 'Создание', color: '#15803d', bg: 'rgba(22,163,74,0.09)' },
  UPDATE: { label: 'Изменение', color: '#0369a1', bg: 'rgba(2,132,199,0.09)' },
  DELETE: { label: 'Удаление', color: '#b91c1c', bg: 'rgba(220,38,38,0.09)' }
};
export const actionOf = (a: string) => ACTIONS[a] || { label: a || '-', color: '#475569', bg: 'rgba(100,116,139,0.08)' };

/** Цвета графиков */
export const CHART = {
  sum: '#2563eb',
  count: '#0ea5e9',
  qty: '#0d9488',
  prev: '#94a3b8',
  cum: '#7c3aed',
  expired: '#dc2626',
  d30: '#ea580c',
  d90: '#f59e0b',
  later: '#16a34a',
  created: '#16a34a',
  updatePlus: '#0ea5e9',
  updateMinus: '#f59e0b',
  deleted: '#dc2626',
  balance: '#7c3aed',
  grid: '#eef2f7',
  axis: '#94a3b8'
};

/** Палитра категориальных рядов (сдержанная, различимая) */
export const PALETTE = ['#2563eb', '#0d9488', '#7c3aed', '#ea580c', '#0284c7', '#db2777', '#65a30d', '#d97706', '#4f46e5', '#0891b2', '#be123c', '#57534e', '#9333ea', '#15803d', '#b45309', '#94a3b8'];

export const EXPORT_FORMATS: Array<{ value: ExportFormat; label: string }> = [
  { value: 'xlsx', label: 'Excel (.xlsx)' },
  { value: 'csv', label: 'CSV' }
];

/** Колонки выгрузки - ключи совпадают с серверными (EXPORT_COLUMNS в journalV2.js) */
export const EXPORT_COLUMNS: Array<{ key: string; label: string; xlsxOnly?: boolean }> = [
  { key: 'pharmacy', label: 'Аптека' },
  { key: 'region', label: 'Регион' },
  { key: 'name', label: 'Наименование' },
  { key: 'producer', label: 'Производитель' },
  { key: 'expiry', label: 'Срок годности' },
  { key: 'daysLeft', label: 'Дней до истечения' },
  { key: 'serial', label: 'Серия' },
  { key: 'qty', label: 'Кол-во уп.' },
  { key: 'qtyPcs', label: 'Кол-во шт.' },
  { key: 'price', label: 'Цена' },
  { key: 'totalSum', label: 'Сумма' },
  { key: 'manager', label: 'Менеджер' },
  { key: 'category', label: 'Категория' },
  { key: 'priceGroup', label: 'Цен. группа' },
  { key: 'rxotc', label: 'RX/OTC' },
  { key: 'code', label: 'Код' },
  { key: 'barcode', label: 'ШК' },
  { key: 'barcodeImage', label: 'Штрихкод (картинка)', xlsxOnly: true },
  { key: 'comment', label: 'Комментарий' },
  { key: 'recordDate', label: 'Дата записи' },
  { key: 'updateDate', label: 'Дата изменения' },
  { key: 'createdBy', label: 'Кем создан' }
];

/** Набор колонок по умолчанию - как на сервере: всё, кроме картинок штрихкодов и даты изменения */
export const DEFAULT_EXPORT_COLUMNS = EXPORT_COLUMNS.filter(c => c.key !== 'barcodeImage' && c.key !== 'updateDate').map(c => c.key);

export type ColumnId =
  | 'pharmacy' | 'region' | 'name' | 'producer' | 'expiry' | 'daysLeft' | 'serial' | 'qty' | 'qtyPcs' | 'price' | 'totalSum'
  | 'manager' | 'category' | 'priceGroup' | 'rxotc' | 'code' | 'barcode' | 'comment' | 'recordDate' | 'updateDate' | 'createdBy';

export interface TableColumn {
  id: ColumnId;
  label: string;
  sort: SortField | null;
  width: number;
  align?: 'right';
  /** Нельзя скрыть */
  required?: boolean;
  /** Скрыта по умолчанию */
  hidden?: boolean;
}

export const TABLE_COLUMNS: TableColumn[] = [
  { id: 'pharmacy', label: 'Аптека', sort: 'pharmacyNum', width: 84, required: true },
  { id: 'region', label: 'Регион', sort: 'cityId', width: 110, hidden: true },
  { id: 'name', label: 'Наименование', sort: 'materialName', width: 280, required: true },
  { id: 'producer', label: 'Производитель', sort: 'producer', width: 170 },
  { id: 'expiry', label: 'Срок годности', sort: 'expiryDate', width: 112, required: true },
  { id: 'daysLeft', label: 'Осталось', sort: 'daysLeft', width: 116 },
  { id: 'serial', label: 'Серия', sort: 'serialNumber', width: 120 },
  { id: 'qty', label: 'Кол-во уп.', sort: 'quantity', width: 96, align: 'right' },
  { id: 'qtyPcs', label: 'Кол-во шт.', sort: 'quantityPcs', width: 96, align: 'right' },
  { id: 'price', label: 'Цена', sort: 'price', width: 104, align: 'right' },
  { id: 'totalSum', label: 'Сумма', sort: 'totalSum', width: 120, align: 'right', required: true },
  { id: 'manager', label: 'Менеджер', sort: 'managerName', width: 150 },
  { id: 'category', label: 'Категория', sort: 'categoryName', width: 140 },
  { id: 'priceGroup', label: 'Цен. группа', sort: 'priceGroup', width: 120, hidden: true },
  { id: 'rxotc', label: 'RX/OTC', sort: 'rxotc', width: 90 },
  { id: 'code', label: 'Код', sort: 'nomenclatureCode', width: 100, hidden: true },
  { id: 'barcode', label: 'ШК', sort: 'barcode', width: 130, hidden: true },
  { id: 'comment', label: 'Комментарий', sort: 'comment', width: 170 },
  { id: 'recordDate', label: 'Дата записи', sort: 'recordDate', width: 124 },
  { id: 'updateDate', label: 'Изменено', sort: 'updateDate', width: 136, hidden: true },
  { id: 'createdBy', label: 'Кем создан', sort: 'createdBy', width: 130, hidden: true }
];

/** Есть ли хотя бы один фильтр, кроме периода (период - отдельный выбор, «Сброс» его не трогает) */
export const hasAnyFilter = (f: JournalFilters): boolean => (
  Boolean(f.search.trim()) || f.pharmacies.length > 0 || Boolean(f.bucket) || Boolean(f.expiryFrom) || Boolean(f.expiryTo) ||
  Object.values(f.picks).some(Boolean)
);

/** Фильтры под кнопкой «Ещё фильтры» - для бейджа со счётчиком */
export const countAdvancedFilters = (f: JournalFilters): number => (
  (f.bucket ? 1 : 0) + (f.expiryFrom || f.expiryTo ? 1 : 0) + Object.values(f.picks).filter(Boolean).length
);
